import { Image, Text, View } from '@react-pdf/renderer'
import { AutoComplete, DatePicker, Form, Input } from 'antd'
import { CantileverButton } from 'components/CantileverButton'
import dayjs from 'dayjs'
import {
  CoverSectionData,
  MainInfo,
  RugReportSectionId,
} from 'pages/reporting/reports/rug/rugReportData'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import {
  ReportedAssetType,
  ReportMaterialType,
  XAxisStartLocationOptions,
  YAxisStartLocationOptions,
} from 'pages/reporting/reports/rug/types'
import GeckoLogo from 'pages/reporting/shared/pdfGeneration/components/shared/GeckoLogo'
import { GradientStripe } from 'pages/reporting/shared/pdfGeneration/components/shared/GradientStripe'
import { PageFooter } from 'pages/reporting/shared/pdfGeneration/components/shared/PageFooter'
import { ReportPage } from 'pages/reporting/shared/pdfGeneration/components/shared/ReportPage'
import { PDFStyles } from 'pages/reporting/shared/pdfGeneration/styles/PDFStyles'
import { formatDate } from 'pages/reporting/shared/pdfGeneration/utils/formatters'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import { PdfProps, ReportSection } from 'pages/reporting/shared/types'
import React, { useMemo } from 'react'
import { autocompleteFilterOption } from 'utils/fde/antD'

const REPORT_DATE_FORMAT = 'MM/DD/YYYY'

const requiredCoverPageFields = [
  'mainInfo.customerName',
  'mainInfo.customerLocation',
  'mainInfo.assetName',
  'mainInfo.assetType',
  'mainInfo.assetId',
  'coverSectionData.inspectionStartDate',
  'coverSectionData.inspectionEndDate',
]

const ContributorRow: React.FC<{
  contributorType: string
  contributor?: {
    name?: string
    certification?: string
  }
  signature?: {
    name?: string
    qualifications?: string
    timestamp?: string
    signature?: string
  }
}> = ({ contributorType, contributor, signature }) => {
  return (
    <>
      <View style={PDFStyles.contributorRow}>
        <Text style={PDFStyles.contributorHeaderText}>{contributorType}</Text>
        <Text style={PDFStyles.contributorHeaderText}>Certification</Text>
        <Text style={PDFStyles.contributorHeaderText}>Date</Text>
        <Text style={PDFStyles.contributorHeaderText}>Signature</Text>
      </View>
      <View style={PDFStyles.contributorBox}>
        <Text style={PDFStyles.contributorText}>
          {signature?.name || contributor?.name || ''}
        </Text>
        <Text style={PDFStyles.contributorText}>
          {signature?.qualifications || contributor?.certification || ''}
        </Text>
        <Text style={PDFStyles.contributorText}>
          {signature?.timestamp ? formatDate(signature.timestamp) : ''}
        </Text>
        {signature?.signature ? (
          <Image src={signature.signature} style={PDFStyles.signatureImage} />
        ) : (
          <Text style={PDFStyles.contributorText}></Text>
        )}
      </View>
    </>
  )
}

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData } = useRugReportOps()

  const signoff = useMemo(() => formData.signoff, [formData.signoff])
  const mainInfo = useMemo(() => formData.mainInfo, [formData.mainInfo])
  const coverData = useMemo(
    () => formData.coverSectionData,
    [formData.coverSectionData]
  )

  const oneDayInspection = useMemo(() => {
    const startDate = dayjs(coverData.inspectionStartDate)
    const endDate = dayjs(coverData.inspectionEndDate)
    return startDate.isSame(endDate, 'day')
  }, [coverData.inspectionStartDate, coverData.inspectionEndDate])

  const isPrelim = useMemo(
    () =>
      !(
        formData.signoff?.reviewerSignoff &&
        formData.signoff?.supervisorSignoff &&
        formData.signoff?.dataAnalystSignoff
      ),
    [
      formData.signoff?.reviewerSignoff,
      formData.signoff?.supervisorSignoff,
      formData.signoff?.dataAnalystSignoff,
    ]
  )

  const methodTitle = 'Rapid Ultrasonic Gridding (RUG) Examination' // RUG specific

  const getDatesOfInspectionTitle = () => {
    if (!coverData.inspectionStartDate || !coverData.inspectionEndDate) {
      return 'Not specified'
    }

    const startDate = dayjs(coverData.inspectionStartDate).format(
      REPORT_DATE_FORMAT
    )
    const endDate = dayjs(coverData.inspectionEndDate).format(
      REPORT_DATE_FORMAT
    )

    if (oneDayInspection) {
      return startDate
    }

    return `${startDate} - ${endDate}`
  }

  return (
    <ReportPage
      id='cover'
      wrap={false}
      tracker={{
        sectionId: RugReportSectionId.COVER,
        onRender,
        registerOnce: false,
      }}
      contentStyle={PDFStyles.content}
      footer={
        <PageFooter
          jobNumber={mainInfo.assetId}
          revisionNumber={formData.revisionMetadata.revisionNumber}
          prelim={isPrelim}
        />
      }>
      {/* Header with Gecko logo and gradient stripe */}
      <View style={PDFStyles.coverHeader}>
        <GeckoLogo />
        <View style={PDFStyles.coverHeaderContent}>
          <Text style={PDFStyles.coverHeaderText}>
            {`${isPrelim ? 'Preliminary' : 'Automated'} Ultrasonics Inspection Report`}
          </Text>
          <GradientStripe />
        </View>
      </View>

      {/* Customer Section */}
      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageDash} />
          <Text style={PDFStyles.coverPageTitleText}>Customer</Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueTextLarge}>
            {mainInfo.customerName}
          </Text>
          <Text style={PDFStyles.coverPageValueTextLarge}>
            {mainInfo.customerLocation}
          </Text>
        </View>
      </View>

      {/* Method Section */}
      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageShortDash} />
          <Text style={PDFStyles.coverPageTitleText}>Method</Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueText}>{methodTitle}</Text>
        </View>
      </View>

      {/* Asset Section */}
      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageDash} />
          <Text style={PDFStyles.coverPageBoldTitleText}>Asset</Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueText}>
            {`${mainInfo.assetName} - ${mainInfo.assetType}`}
          </Text>
        </View>
      </View>

      {/* Job Number Section */}
      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageShortDash} />
          <Text style={PDFStyles.coverPageTitleText}>Gecko Job Number</Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueText}>{mainInfo.assetId}</Text>
        </View>
      </View>

      {/* Inspection Dates Section */}
      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageDash} />
          <Text style={PDFStyles.coverPageTitleText}>
            {oneDayInspection ? 'Date of Inspection' : 'Dates of Inspection'}
          </Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueText}>
            {getDatesOfInspectionTitle()}
          </Text>
        </View>
      </View>

      {/* Signature Section */}
      <View style={PDFStyles.signatureSection}>
        {/* Field Technician Row */}
        <ContributorRow
          contributorType='Field Technician'
          signature={signoff?.supervisorSignoff ?? undefined}
        />

        <ContributorRow
          contributorType='Data Analyst'
          signature={signoff?.dataAnalystSignoff ?? undefined}
        />

        {/* Reviewer Row */}
        <ContributorRow
          contributorType='Reviewed By'
          signature={signoff?.reviewerSignoff ?? undefined}
        />
      </View>
    </ReportPage>
  )
}

interface CoverPageFormValues
  extends
    Omit<MainInfo, 'componentAxisStartLocations'>,
    Omit<CoverSectionData, 'inspectionStartDate' | 'inspectionEndDate'> {
  inspectionStartDate: dayjs.Dayjs
  inspectionEndDate: dayjs.Dayjs
  [key: `xAxis_${string}` | `yAxis_${string}`]: string | undefined
}

const RenderReact: React.FC = () => {
  const [form] = Form.useForm<CoverPageFormValues>()
  const {
    formData,
    handleCoverSectionFinish,
    handleMainInfoFinish,
    selectedAssetModel,
    selectedComponents,
  } = useRugReportOps()

  const initialValues = React.useMemo(() => {
    const componentAxisValues: Record<string, string> = {}
    if (formData.mainInfo.componentAxisStartLocations) {
      Object.entries(formData.mainInfo.componentAxisStartLocations).forEach(
        ([componentId, locations]) => {
          componentAxisValues[`xAxis_${componentId}`] =
            locations.xAxisStartLocation
          componentAxisValues[`yAxis_${componentId}`] =
            locations.yAxisStartLocation
        }
      )
    }

    return {
      ...formData.mainInfo,
      ...formData.coverSectionData,
      ...componentAxisValues,
      inspectionStartDate: formData.coverSectionData.inspectionStartDate
        ? dayjs(formData.coverSectionData.inspectionStartDate)
        : undefined,
      inspectionEndDate: formData.coverSectionData.inspectionEndDate
        ? dayjs(formData.coverSectionData.inspectionEndDate)
        : undefined,
    }
  }, [formData.mainInfo, formData.coverSectionData])

  const onFinish = async (values: CoverPageFormValues) => {
    const componentAxisStartLocations: Record<
      string,
      { xAxisStartLocation: string; yAxisStartLocation: string }
    > = {}

    selectedComponents?.forEach((component) => {
      const xKey = `xAxis_${component.id}` as const
      const yKey = `yAxis_${component.id}` as const
      if (values[xKey] !== undefined || values[yKey] !== undefined) {
        componentAxisStartLocations[component.id] = {
          xAxisStartLocation: values[xKey] || '',
          yAxisStartLocation: values[yKey] || '',
        }
      }
    })

    const mainInfoUpdate: MainInfo = {
      assetName: values.assetName,
      assetId: values.assetId,
      assetType: values.assetType,
      assetMaterial: values.assetMaterial,
      customerName: values.customerName,
      customerLocation: values.customerLocation,
      inspectionTemperature: values.inspectionTemperature,
      componentAxisStartLocations,
    }

    const coverData: CoverSectionData = {
      inspectionStartDate:
        values.inspectionStartDate.format(REPORT_DATE_FORMAT),
      inspectionEndDate: values.inspectionEndDate.format(REPORT_DATE_FORMAT),
    }

    await handleCoverSectionFinish(coverData)
    await handleMainInfoFinish(mainInfoUpdate)
  }

  return (
    <StyledForm
      form={form}
      name='rugCoverPageForm'
      onFinish={onFinish}
      layout='vertical'
      initialValues={initialValues}>
      <Form.Item name='customerName' label='Customer Name'>
        <Input />
      </Form.Item>

      <Form.Item name='customerLocation' label='Customer Location'>
        <Input />
      </Form.Item>

      <Form.Item name='assetName' label='Asset Name'>
        <Input />
      </Form.Item>

      <Form.Item name='assetType' label='Asset Type'>
        <AutoComplete
          options={Object.values(ReportedAssetType).map((status) => {
            return { value: status }
          })}
          placeholder='Select Asset Type'
          filterOption={autocompleteFilterOption}
        />
      </Form.Item>

      <Form.Item name='assetId' label='Gecko Job Number'>
        <Input />
      </Form.Item>

      <Form.Item name='assetMaterial' label='Asset Material'>
        <AutoComplete
          options={Object.values(ReportMaterialType).map((status) => {
            return { value: status }
          })}
          placeholder='Select Asset Material'
          filterOption={autocompleteFilterOption}
        />
      </Form.Item>

      <Form.Item name='inspectionTemperature' label='Inspection Temperature'>
        <Input />
      </Form.Item>

      {selectedComponents && selectedComponents.length > 0 && (
        <>
          {selectedComponents.map((component) => {
            const assetType = selectedAssetModel?.data.type

            return (
              <>
                <Form.Item
                  name={`xAxis_${component.id}`}
                  label={`X-Axis Start Location (${component.data.name})`}>
                  <AutoComplete
                    options={(assetType
                      ? (XAxisStartLocationOptions[assetType] ?? [])
                      : []
                    ).map((option) => ({ value: option }))}
                    placeholder='Select or Type Location'
                    filterOption={autocompleteFilterOption}
                  />
                </Form.Item>
                <Form.Item
                  name={`yAxis_${component.id}`}
                  label={`Y-Axis Start Location (${component.data.name})`}>
                  <AutoComplete
                    options={(assetType
                      ? (YAxisStartLocationOptions[assetType] ?? [])
                      : []
                    ).map((option) => ({ value: option }))}
                    placeholder='Select or Type Location'
                    filterOption={autocompleteFilterOption}
                  />
                </Form.Item>
              </>
            )
          })}
        </>
      )}

      <Form.Item name='inspectionStartDate' label='Start Date'>
        <DatePicker style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item name='inspectionEndDate' label='End Date'>
        <DatePicker style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item>
        <CantileverButton
          primary
          onClick={() => form.submit()}
          style={{ width: '100%' }}>
          Save Cover Page
        </CantileverButton>
      </Form.Item>
    </StyledForm>
  )
}

export const Cover: ReportSection = {
  RenderReact,
  required: true,
  RenderPDF,
  id: RugReportSectionId.COVER,
  description: 'Basic report information and metadata',
  title: 'Cover Page',
  requiredFields: requiredCoverPageFields,
}
